// src/components/portal/login.js
import React, { useState } from 'react';
import { useTheme, ThemeProvider } from '@mui/material/styles';
import {
  Button,
  TextField,
  Box,
  Typography,
  Container,
  Paper,
  Grid,
  CircularProgress
} from "@mui/material";
import Header from '../../Header';

import portalSession from '../portal_services/portal_session';

const Login = ({ onLoginSuccess }) => {
  const theme = useTheme();
  const [loginId, setLoginId] = useState("");
  const [authCode, setAuthCode] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loading, setLoading] = useState(false);

  // API configuration
  const BASE_URL = portalSession.getBaseURL();

  const handleLogin = async () => {
    // Reset any previous error messages
    setLoginError("");
    
    // Simple validation
    if (!loginId.trim()) {
      setLoginError("Please enter your Login ID");
      return;
    }
    if (!authCode.trim()) {
      setLoginError("Please enter your Authentication Code");
      return;
    }
    
    try {
      setLoading(true);
      const response = await fetch(`${BASE_URL}/authenticate`, {
        method: 'POST',
        headers: {
            'X-Customer-ID': loginId.trim(),
            'X-Customer-Token': authCode.trim(),
            'Content-Type': 'application/json',
          },
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Store session data
        portalSession.setSession(loginId.trim(), authCode.trim());
        
        // Authentication successful - call the success callback
        onLoginSuccess(loginId.trim());
      // }

      // if (response.ok && data.success) {
      //   // Authentication successful - call the success callback
      //   onLoginSuccess(data.customer_id);
      } else {
        // Authentication failed
        setLoginError("Incorrect credentials. Please check your Login ID and Authentication Code.");
      }
    } catch (error) {
      console.error('Authentication error:', error);
      setLoginError("Unable to connect to the server. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  // Handle Enter key press
  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !loading) {
      handleLogin();
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        bgcolor: "grey.100"
      }}
    >
      <Header />
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 2
        }}
      >
        <Paper 
          elevation={3} 
          sx={{ 
            maxWidth: "md", 
            width: "100%", 
            p: 4, 
            borderRadius: 2 
          }}
        >
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <Typography variant="h4" fontWeight="bold" mb={1}>
              Customer Portal
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage your content in one place.
            </Typography>
          </Box>
          
          <Grid container spacing={2} direction="column">
            <Grid item>
              <TextField
                label="Login ID"
                variant="outlined"
                fullWidth
                value={loginId}
                onChange={(e) => setLoginId(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={loading}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused fieldset': {
                      borderColor: '#0a7c72',
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#0a7c72',
                  }
                }}
              />
            </Grid>
            <Grid item>
              <TextField
                label="Authentication Code"
                variant="outlined"
                fullWidth
                type="password"
                value={authCode}
                onChange={(e) => setAuthCode(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={loading}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused fieldset': {
                      borderColor: '#0a7c72',
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#0a7c72',
                  }
                }}
              />
            </Grid>
            {loginError && (
              <Grid item>
                <Typography variant="body2" color="error">
                  {loginError}
                </Typography>
              </Grid>
            )}
            <Grid item sx={{ mt: 2 }}>
              <Button 
                variant="contained" 
                fullWidth
                onClick={handleLogin}
                disabled={loading}
                sx={{ 
                  bgcolor: '#0a7c72', 
                  py: 1.5,
                  '&:hover': { 
                    bgcolor: '#086860' 
                  },
                  '&:disabled': {
                    bgcolor: '#0a7c72',
                    opacity: 0.6
                  }
                }}
              >
                {loading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={20} color="inherit" />
                    Authenticating...
                  </Box>
                ) : (
                  'Login'
                )}
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Box>
    </Box>
  );
};

export default Login;